import { z } from 'zod';
import { ValidationPatterns } from '../shared/common';

/**
 * Domain schemas for leads entities
 * Using Base+Variants pattern for scalability
 */

/**
 * Base schema for lead entity data
 * Contains all possible fields that may be returned in different contexts
 */
const BaseLeadSchema = z.object({
  id: z.string(),
  email: ValidationPatterns.email,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

/**
 * Lead schema variants for different contexts
 */
export const LeadSchemas = {
  // Full lead data (internal use)
  full: BaseLeadSchema,

  // Public lead data (minimal exposure)
  public: BaseLeadSchema.pick({
    id: true,
    email: true,
  }),

  // For creation requests (no id, timestamps)
  create: BaseLeadSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  }),
};

/**
 * Response schemas for lead operations
 */
export const LeadResponseSchemas = {
  // Subscription success response
  subscription: z.object({
    message: z.string(),
  }),

  // Created lead response (could include the created lead data)
  created: z.object({
    message: z.string(),
    lead: LeadSchemas.public.optional(),
  }),
};

// Export types
export type Lead = z.infer<typeof LeadSchemas.full>;
export type PublicLead = z.infer<typeof LeadSchemas.public>;
export type CreateLead = z.infer<typeof LeadSchemas.create>;
export type LeadSubscriptionResponse = z.infer<
  typeof LeadResponseSchemas.subscription
>;
export type CreatedLeadResponse = z.infer<typeof LeadResponseSchemas.created>;
